'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useMemo } from 'react'

import Icon from '../../components/Icon'
import { useAccountBottomNavOffset, useReviewsWidgetOffset } from '../../hooks/useAccountBottomNav'
import { useWindowSize } from '../../hooks/useWindowSize'

type AccountBottomNavigationItem = {
  id?: string | null
  title: string
  icon: string
  url: string
}

type AccountBottomNavigationProps = {
  items: AccountBottomNavigationItem[]
  visiblePathPrefixes?: string[]
  ariaLabel?: string
}

export default function AccountBottomNavigation({
  items,
  visiblePathPrefixes = ['/account'],
  ariaLabel = 'Account',
}: AccountBottomNavigationProps) {
  const pathname = usePathname()
  const { isMobile } = useWindowSize()

  const isVisible =
    isMobile && visiblePathPrefixes.some(prefix => pathname?.startsWith(prefix)) && items.length > 0
  const accountNavOffset = 'calc(55px + env(safe-area-inset-bottom))'
  const reviewsWidgetOffset = 'calc(55px + 10px + env(safe-area-inset-bottom))'

  // Set CSS variable for bottom nav offset to be used by other components like the WhatsApp floating button and set creator
  useAccountBottomNavOffset(isVisible, accountNavOffset)

  // Reset the bottom position of the Reviews.io floating widget, to avoid it being in front of the account bottom nav
  useReviewsWidgetOffset(isVisible, reviewsWidgetOffset)

  const activeUrl = useMemo(() => {
    if (!pathname) return

    const currentSegments = pathname.split('/')

    return items
      .map(item => {
        const segments = item.url.split('/')
        const matchCount = segments.filter((segment, i) => segment === currentSegments[i]).length
        return { url: item.url, matchCount }
      })
      .sort((a, b) => b.matchCount - a.matchCount)[0]?.url
  }, [items, pathname])

  if (!isVisible || items.length === 0) return null

  return (
    <>
      <div className='h-[calc(55px+env(safe-area-inset-bottom))] md:hidden' aria-hidden='true' />
      <nav
        aria-label={ariaLabel}
        className='fixed bottom-0 left-0 right-0 z-40 min-h-[55px] border-t border-lightgray bg-white/95 backdrop-blur md:hidden'
      >
        <div className='mx-auto flex max-w-md items-center justify-around px-3 pb-[env(safe-area-inset-bottom)]'>
          {items.map(item => {
            const isActive = activeUrl === item.url

            return (
              <Link
                key={item.id ?? item.url ?? item.title}
                href={item.url}
                aria-current={isActive ? 'page' : undefined}
                className={clsx(
                  'flex flex-1 flex-col items-center gap-1 py-1.5 text-xs no-underline transition-colors hover:no-underline',
                  isActive ? 'font-semibold text-black' : 'text-gray',
                )}
              >
                <Icon name={item.icon} size={24} />
                <span className={'text-center leading-tight'}>{item.title}</span>
              </Link>
            )
          })}
        </div>
      </nav>
    </>
  )
}
